import type { FormConfig, StepConfig, FieldConfig } from '../types';
import type { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import formFieldsMappingJson from '../formFieldsMapping.json';

/** localStorage key: when set to 'true', fields updated from CMS get _cmsMappedKeys / _cmsValidationKeys / values for debug UI */
const DEBUG_CMS_SOURCE_KEY = 'willsFormDebugCmsSource';

function getDebugCmsSourceEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(DEBUG_CMS_SOURCE_KEY) === 'true';
  } catch {
    return false;
  }
}

interface FormMapping {
  title?: string;
}

interface StepMapping {
  stepId: string;
  title?: string;
  subTitle?: string;
  description?: string;
  note?: string;
}

interface FieldMapping {
  stepId: string;
  fieldId: string;
  mappings: Record<string, string | Record<string, string> | undefined>;
}

interface UITextMapping {
  buttons?: {
    back?: string;
    next?: string;
    skip?: string;
    submit?: string;
    edit?: string;
    save?: string;
    cancel?: string;
    add?: string;
    addAnother?: string;
    start?: string;
    finish?: string;
    nextSection?: string;
    addGuardian?: string;
    addNewBeneficiary?: string;
  };
  status?: {
    completed?: string;
    inProgress?: string;
    toDo?: string;
  };
  progress?: {
    sections?: Array<{
      sectionId: string;
      stepTemplate?: string;
      timeRemaining?: string;
    }>;
  };
  actions?: {
    startSectionTemplate?: string;
  };
}

interface CardMapping {
  cardId: string;
  mappings: Record<string, string>;
}

interface FormFieldsMapping {
  form?: FormMapping;
  steps?: StepMapping[];
  fields?: FieldMapping[];
  cards?: CardMapping[];
  uiText?: UITextMapping;
}

/**
 * Finds an object in an array by matching the 'name' property
 */
function findInArrayByName(arr: unknown[], nameToMatch: string): unknown | null {
  if (!Array.isArray(arr)) return null;

  return (
    arr.find((item: unknown) => {
      if (typeof item === 'object' && item !== null) {
        // Check direct 'name' property
        if ('name' in item && (item as { name: string }).name === nameToMatch) {
          return true;
        }
        // Check 'fields.name.value' structure (Sitecore format)
        if ('fields' in item) {
          const fields = (item as { fields?: { name?: { value?: string } } }).fields;
          if (fields?.name?.value === nameToMatch) {
            return true;
          }
        }
      }
      return false;
    }) || null
  );
}

/**
 * Resolves a nested path string with array matching by name
 * Supports:
 * - "formfields[name=formTitle].value" - find in formfields array by name
 * - "formfields.aboutYouForms[name=PersonalDetailForm].fields.formTitle.value" - nested array matching
 * - "formfields.fields.formTitle.value" - traditional dot notation (backward compatible)
 */
function getValueByPath(obj: unknown, path: string): string | undefined | unknown {
  if (!path || !obj) return undefined;

  const keys = path.split('.');
  let current: unknown = obj;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    if (current === null || current === undefined) {
      return undefined;
    }

    // Handle array matching: "arrayName[name=fieldName]"
    const arrayMatch = key.match(/^([^[]+)\[name=([^\]]+)\]$/);
    if (arrayMatch) {
      const arrayName = arrayMatch[1];
      const nameToMatch = arrayMatch[2];

      // Get the array
      if (typeof current === 'object' && arrayName in current) {
        const array = (current as Record<string, unknown>)[arrayName];

        if (Array.isArray(array)) {
          // Find object in array where name matches
          const found = findInArrayByName(array, nameToMatch);

          if (found) {
            current = found;
            // If this is the last key and we want the whole object (for options), return it
            if (i === keys.length - 1) {
              return found;
            }
            continue;
          }
        }
      }
      return undefined;
    }

    // Traditional dot notation
    if (typeof current === 'object' && key in current) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }

  // Return the value
  if (typeof current === 'string' || typeof current === 'number' || typeof current === 'boolean') {
    return String(current);
  }

  // Return array/object as-is (for options)
  return current;
}

/**
 * Converts Sitecore options format to formConfig options format
 * Handles both selectOptionsAndSection and radioOptions arrays
 */
function convertOptionsToFormConfigFormat(
  optionsValue: unknown
): Array<{ label: string; value: string; shortText?: string }> | undefined {
  if (!optionsValue) return undefined;

  // If it's already an array
  if (Array.isArray(optionsValue)) {
    return optionsValue.map((opt: unknown) => {
      if (typeof opt === 'object' && opt !== null) {
        // Handle Sitecore FormField_Item format with fields
        if ('fields' in opt) {
          const fields = (
            opt as {
              fields?: {
                text?: { value?: string };
                value?: { value?: string };
                shortText?: { value?: string };
              };
            }
          ).fields;
          return {
            label:
              fields?.text?.value ||
              fields?.value?.value ||
              (opt as { displayName?: string }).displayName ||
              '',
            value:
              fields?.value?.value ||
              (opt as { name?: string }).name ||
              (opt as { id?: string }).id ||
              '',
            shortText: (fields?.shortText?.value || '') as string,
          };
        }
        // Handle direct object format
        if ('label' in opt && 'value' in opt) {
          return {
            label: String((opt as { label: unknown }).label),
            value: String((opt as { value: unknown }).value),
            shortText: (opt as { shortText?: string }).shortText || '',
          };
        }
        // Fallback to displayName/name
        return {
          label:
            (opt as { displayName?: string }).displayName || (opt as { name?: string }).name || '',
          value: (opt as { name?: string }).name || (opt as { id?: string }).id || '',
          shortText: (opt as { shortText?: string }).shortText || '',
        };
      }
      return {
        label: String(opt),
        value: String(opt),
        shortText: String(opt),
      };
    });
  }

  // If it's a string, try to parse as JSON
  if (typeof optionsValue === 'string') {
    try {
      const parsed = JSON.parse(optionsValue);
      if (Array.isArray(parsed)) {
        return convertOptionsToFormConfigFormat(parsed);
      }
    } catch {
      // If parsing fails, return undefined
    }
  }

  return undefined;
}

/**
 * Sets a nested property value in an object using dot notation
 * (e.g., "validation.required" sets obj.validation.required)
 */
function setValueByPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  const lastKey = keys.pop();
  if (!lastKey) return;

  let current: Record<string, unknown> = obj;

  for (const key of keys) {
    if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  current[lastKey] = value;
}

/**
 * Gets a nested property value from an object using dot notation
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }

  return current;
}

/**
 * Checks if a value is blank, null, or undefined
 */
function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '' || String(value).trim() === '';
}

/**
 * Applies form-level mappings
 */
function applyFormMappings(
  formConfig: FormConfig,
  formfields: CustomForm.Form['fields'],
  mappings: FormMapping
): FormConfig {
  const updatedConfig = { ...formConfig };

  if (mappings.title) {
    const sitecoreValue = getValueByPath(formfields, mappings.title);
    if (!isBlank(sitecoreValue) && typeof sitecoreValue === 'string') {
      updatedConfig.title = sitecoreValue;
    }
  }

  return updatedConfig;
}

/**
 * Applies step-level mappings
 */
function applyStepMappings(
  formConfig: FormConfig,
  formfields: CustomForm.Form['fields'],
  mappings: StepMapping[]
): FormConfig {
  if (!mappings || mappings.length === 0) return formConfig;

  const updatedConfig = { ...formConfig };
  const updatedSteps = [...updatedConfig.steps];

  mappings.forEach((mapping) => {
    const stepIndex = updatedSteps.findIndex((step) => step.id === mapping.stepId);
    if (stepIndex === -1) return;

    const step = updatedSteps[stepIndex];
    const updatedStep: StepConfig = { ...step };

    if (mapping.title) {
      const sitecoreValue = getValueByPath(formfields, mapping.title);
      if (!isBlank(sitecoreValue) && typeof sitecoreValue === 'string') {
        updatedStep.title = sitecoreValue;
      }
    }

    if (mapping.subTitle) {
      const sitecoreValue = getValueByPath(formfields, mapping.subTitle);
      if (!isBlank(sitecoreValue) && typeof sitecoreValue === 'string') {
        updatedStep.subTitle = sitecoreValue;
      }
    }

    if (mapping.description) {
      const sitecoreValue = getValueByPath(formfields, mapping.description);
      if (!isBlank(sitecoreValue) && typeof sitecoreValue === 'string') {
        updatedStep.description = sitecoreValue;
      }
    }

    if (mapping.note) {
      const sitecoreValue = getValueByPath(formfields, mapping.note);
      if (!isBlank(sitecoreValue) && typeof sitecoreValue === 'string') {
        updatedStep.note = sitecoreValue;
      }
    }

    updatedSteps[stepIndex] = updatedStep;
  });

  updatedConfig.steps = updatedSteps;
  return updatedConfig;
}

/**
 * Applies field-level mappings
 */
function applyFieldMappings(
  formConfig: FormConfig,
  formfields: CustomForm.Form['fields'],
  mappings: FieldMapping[],
  debugCmsSource = false
): FormConfig {
  if (!mappings || mappings.length === 0) return formConfig;

  const updatedConfig = { ...formConfig };
  const updatedSteps = [...updatedConfig.steps];

  mappings.forEach((mapping) => {
    const stepIndex = updatedSteps.findIndex((step) => step.id === mapping.stepId);
    if (stepIndex === -1) return;

    const step = updatedSteps[stepIndex];
    if (!step.fieldGroups) return;

    const updatedStep = { ...step };
    const updatedFieldGroups = step.fieldGroups.map((fieldGroup) => {
      const updatedFields = fieldGroup.fields.map((field) => {
        if (field.id !== mapping.fieldId) {
          return field;
        }

        // Create a mutable copy of the field
        const updatedField: FieldConfig = JSON.parse(JSON.stringify(field));
        const cmsMappedKeys: string[] = [];
        const cmsValidationKeys: string[] = [];
        const cmsMappedValues: Record<string, unknown> = {};
        const cmsValidationValues: Record<string, unknown> = {};

        // Apply each mapping
        Object.entries(mapping.mappings).forEach(([targetPath, sourcePath]) => {
          if (sourcePath === undefined) return;

          // Handle validation mapping: sourcePath is an object of validationKey -> CMS path
          if (
            targetPath === 'validation' &&
            typeof sourcePath === 'object' &&
            sourcePath !== null
          ) {
            const validationPaths = sourcePath as Record<string, string>;
            const originalValidation = field.validation ?? {};
            updatedField.validation = { ...originalValidation };
            Object.entries(validationPaths).forEach(([validationKey, cmsPath]) => {
              if (!cmsPath) return;
              const sitecoreValue = getValueByPath(formfields, cmsPath);
              if (sitecoreValue === undefined) return;
              const validation = updatedField.validation as Record<string, unknown>;
              if (validationKey === 'required') {
                const str = String(sitecoreValue).trim().toLowerCase();
                const boolVal = str === 'true' || str === '1';
                validation.required = boolVal;
                if (debugCmsSource) cmsValidationValues[validationKey] = boolVal;
              } else if (validationKey === 'min' || validationKey === 'max') {
                const numValue = Number(sitecoreValue);
                if (!isNaN(numValue)) {
                  validation[validationKey] = numValue;
                  if (debugCmsSource) cmsValidationValues[validationKey] = numValue;
                }
              } else if (!isBlank(sitecoreValue) && typeof sitecoreValue === 'string') {
                validation[validationKey] = sitecoreValue;
                if (debugCmsSource) cmsValidationValues[validationKey] = sitecoreValue;
              }
              if (debugCmsSource && !cmsValidationKeys.includes(validationKey)) {
                cmsValidationKeys.push(validationKey);
              }
            });
            if (
              debugCmsSource &&
              cmsValidationKeys.length > 0 &&
              !cmsMappedKeys.includes('validation')
            ) {
              cmsMappedKeys.push('validation');
              cmsMappedValues['validation'] = { ...cmsValidationValues };
            }
            return; // Skip other processing for validation
          }

          // sourcePath must be a string for remaining mappings
          if (typeof sourcePath !== 'string') return;
          const sitecoreValue = getValueByPath(formfields, sourcePath);

          // Handle options mapping separately
          if (targetPath === 'options') {
            const convertedOptions = convertOptionsToFormConfigFormat(sitecoreValue);
            if (convertedOptions && convertedOptions.length > 0) {
              const originalOptions = field.options ?? [];
              updatedField.options = convertedOptions.map((opt) => {
                const optVal = (opt.value ?? '').trim().toLowerCase();
                const optLabel = (opt.label ?? '').trim().toLowerCase();
                const match = originalOptions.find(
                  (o) =>
                    (o.value ?? '').trim().toLowerCase() === optVal ||
                    (o.label ?? '').trim().toLowerCase() === optLabel
                );
                return {
                  ...opt,
                  ...(match?.showIf != null && { showIf: match.showIf }),
                };
              });
              if (debugCmsSource) {
                cmsMappedKeys.push('options');
                cmsMappedValues['options'] = updatedField.options;
              }
            }
            return; // Skip other processing for options
          }

          // Only update if Sitecore value exists and is not blank
          if (!isBlank(sitecoreValue) && typeof sitecoreValue === 'string') {
            // Handle nested paths like "validation.max"
            if (targetPath.includes('.')) {
              // For nested paths, we need to ensure the parent object exists
              const pathParts = targetPath.split('.');
              const parentPath = pathParts.slice(0, -1).join('.');
              const propertyName = pathParts[pathParts.length - 1];

              // Get or create parent object
              let parent: Record<string, unknown> = updatedField as unknown as Record<
                string,
                unknown
              >;
              if (parentPath) {
                const parentValue = getNestedValue(
                  updatedField as unknown as Record<string, unknown>,
                  parentPath
                );
                if (parentValue && typeof parentValue === 'object') {
                  parent = parentValue as Record<string, unknown>;
                } else {
                  setValueByPath(
                    updatedField as unknown as Record<string, unknown>,
                    parentPath,
                    {}
                  );
                  parent = getNestedValue(
                    updatedField as unknown as Record<string, unknown>,
                    parentPath
                  ) as Record<string, unknown>;
                }
              }

              // Set the value
              if (propertyName === 'max' && parentPath === 'validation') {
                // Special handling for numeric values
                const numValue = Number(sitecoreValue);
                if (!isNaN(numValue)) {
                  (updatedField.validation = updatedField.validation || {})[propertyName] =
                    numValue;
                  if (debugCmsSource) cmsMappedValues[targetPath] = numValue;
                }
              } else {
                parent[propertyName] = sitecoreValue;
                if (debugCmsSource) cmsMappedValues[targetPath] = sitecoreValue;
              }
              if (debugCmsSource) cmsMappedKeys.push(targetPath);
            } else {
              // Simple property assignment
              if (targetPath === 'type') {
                updatedField.type = sitecoreValue as FieldConfig['type'];
              } else {
                (updatedField as unknown as Record<string, unknown>)[targetPath] = sitecoreValue;
              }
              if (debugCmsSource) {
                cmsMappedKeys.push(targetPath);
                cmsMappedValues[targetPath] = sitecoreValue;
              }
            }
          }
          // If blank, keep the original value from formConfig (already in updatedField)
        });

        if (debugCmsSource && cmsMappedKeys.length > 0) {
          (updatedField as unknown as Record<string, unknown>)._cmsMappedKeys = [
            ...new Set(cmsMappedKeys),
          ];
          (updatedField as unknown as Record<string, unknown>)._cmsMappedValues = cmsMappedValues;
          if (cmsValidationKeys.length > 0) {
            (updatedField as unknown as Record<string, unknown>)._cmsValidationKeys = [
              ...new Set(cmsValidationKeys),
            ];
            (updatedField as unknown as Record<string, unknown>)._cmsValidationValues =
              cmsValidationValues;
          }
        }

        return updatedField;
      });

      return { ...fieldGroup, fields: updatedFields };
    });

    updatedStep.fieldGroups = updatedFieldGroups;
    updatedSteps[stepIndex] = updatedStep;
  });

  updatedConfig.steps = updatedSteps;
  return updatedConfig;
}

function applyCardMappings(
  formConfig: FormConfig,
  formfields: CustomForm.Form['fields'],
  mappings: CardMapping[]
): FormConfig {
  if (!mappings || mappings.length === 0) return formConfig;

  const updatedConfig = { ...formConfig };

  const mappingByCardId = new Map<string, Record<string, string>>();
  mappings.forEach((m) => {
    if (m?.cardId && m.mappings) mappingByCardId.set(m.cardId, m.mappings);
  });
  const visit = (node: unknown): void => {
    if (!node) return;

    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }

    if (typeof node !== 'object') return;

    const obj = node as Record<string, unknown>;

    const id = typeof obj.id === 'string' ? obj.id : undefined;
    const cardId = typeof obj.cardId === 'string' ? obj.cardId : undefined;

    const matchedId =
      (id && mappingByCardId.has(id) && id) || (cardId && mappingByCardId.has(cardId) && cardId);

    if (matchedId) {
      const cardMappings = mappingByCardId.get(matchedId);
      if (cardMappings) {
        Object.entries(cardMappings).forEach(([targetPath, sourcePath]) => {
          if (!sourcePath) return;

          const sitecoreValue = getValueByPath(formfields, sourcePath);

          // When Sitecore value is blank, leave existing value (e.g. from form.config) unchanged
          if (isBlank(sitecoreValue)) return;

          if (targetPath === 'maxInstances' && typeof sitecoreValue === 'string') {
            const parsed = parseInt(sitecoreValue, 10);
            if (!Number.isNaN(parsed)) {
              obj[targetPath] = parsed;
            }
            return;
          }

          if (typeof sitecoreValue === 'string') {
            if (targetPath.includes('.')) {
              setValueByPath(obj, targetPath, sitecoreValue);
            } else {
              obj[targetPath] = sitecoreValue;
            }
          }
        });
      }
    }
    Object.values(obj).forEach(visit);
  };

  visit(updatedConfig);

  return updatedConfig;
}
/**
 * Applies UI text mappings
 */
function applyUITextMappings(
  formfields: CustomForm.Form['fields'],
  mappings: UITextMapping
): Record<string, string> {
  const uiText: Record<string, string> = {};

  if (mappings.buttons) {
    Object.entries(mappings.buttons).forEach(([key, path]) => {
      if (path) {
        const value = getValueByPath(formfields, path);
        if (!isBlank(value) && typeof value === 'string') {
          uiText[`button.${key}`] = value;
        }
      }
    });
  }

  if (mappings.status) {
    Object.entries(mappings.status).forEach(([key, path]) => {
      if (path) {
        const value = getValueByPath(formfields, path);
        if (!isBlank(value) && typeof value === 'string') {
          uiText[`status.${key}`] = value;
        }
      }
    });
  }

  if (mappings.progress) {
    const progress = mappings.progress as {
      sections?: Array<{
        sectionId: string;
        stepTemplate?: string;
        timeRemaining?: string;
      }>;
    };

    if (Array.isArray(progress.sections)) {
      progress.sections.forEach(
        (section: { sectionId: string; stepTemplate?: string; timeRemaining?: string }) => {
          const { sectionId, stepTemplate, timeRemaining } = section;
          if (!sectionId) return;
          if (stepTemplate) {
            const value = getValueByPath(formfields, stepTemplate);
            if (!isBlank(value) && typeof value === 'string') {
              uiText[`progress.${sectionId}.stepTemplate`] = value;
            }
          }
          if (timeRemaining) {
            const value = getValueByPath(formfields, timeRemaining);
            if (!isBlank(value) && typeof value === 'string') {
              uiText[`progress.${sectionId}.timeRemaining`] = value;
            }
          }
        }
      );
    }
  }

  if (mappings.actions) {
    Object.entries(mappings.actions).forEach(([key, path]) => {
      if (path) {
        const value = getValueByPath(formfields, path);
        if (!isBlank(value) && typeof value === 'string') {
          uiText[`action.${key}`] = value;
        }
      }
    });
  }

  return uiText;
}

/**
 * Maps formfields data to formConfig and returns a cloned, updated config
 * Falls back to original formConfig values if Sitecore values are missing or blank
 */
export function mapFormFieldsToConfig(
  formConfig: FormConfig,
  formfields?: CustomForm.Form['fields']
): FormConfig & { uiText?: Record<string, string> } {
  // Deep clone the formConfig to avoid mutations
  let clonedConfig: FormConfig =
    typeof structuredClone !== 'undefined'
      ? structuredClone(formConfig)
      : JSON.parse(JSON.stringify(formConfig));

  // If no formfields provided, return cloned config as-is
  if (!formfields) {
    return clonedConfig;
  }

  const mapping = formFieldsMappingJson as FormFieldsMapping;

  // Apply form-level mappings
  if (mapping.form) {
    clonedConfig = applyFormMappings(clonedConfig, formfields, mapping.form);
  }

  // Apply step-level mappings
  if (mapping.steps && mapping.steps.length > 0) {
    clonedConfig = applyStepMappings(clonedConfig, formfields, mapping.steps);
  }

  // Apply field-level mappings
  if (mapping.fields && mapping.fields.length > 0) {
    clonedConfig = applyFieldMappings(
      clonedConfig,
      formfields,
      mapping.fields,
      getDebugCmsSourceEnabled()
    );
  }

  // Apply card-level mappings
  if (mapping.cards && mapping.cards.length > 0) {
    clonedConfig = applyCardMappings(clonedConfig, formfields, mapping.cards);
  }

  // Apply UI text mappings
  let uiText: Record<string, string> = {};
  if (mapping.uiText) {
    uiText = applyUITextMappings(formfields, mapping.uiText);
  }

  // Return config with uiText
  return { ...clonedConfig, uiText };
}
